import cn from 'utils/cn'
import Icon from 'core/components/icons/Icon'
import useClipboard from 'core/hooks/utils/useClipboard'
import useUnitBankInfo from 'core/hooks/wallet/useUnitBankInfo'
import { FundingModalFormValues } from 'core/widgets/funding-modal/FundingModal'
import useAmountBrief from 'core/widgets/funding-modal/useAmountBrief'
import numeral from 'numeral'
import BankInfoRow from 'pages/teams/wallet/AchCreditWire/BankInfoRow'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

const ACHCreditWire = () => {
  const { bankName, bankAddress, routingNumber, accountNumber, accountOwner } =
    useUnitBankInfo()
  const { watch } = useFormContext<FundingModalFormValues>()
  const copyToClipboard = useClipboard()
  const amountBrief = useAmountBrief()

  const [amount, fundingMethod, autoPay, fundingInitiated] = watch([
    'amount',
    'funding_method',
    'auto_pay',
    'fundingInitiated'
  ])
  const formattedAmount = useMemo(
    () => numeral(amount).format('$ 0,0.00'),
    [amount]
  )

  const fundingTime = useMemo(
    () => (fundingMethod === 'ach-credit' ? '1-2 business days' : 'Same day'),
    [fundingMethod]
  )
  const onTimeBrief = useMemo(() => {
    // TODO: once we know how it's calculated, we can remove this hardcoded value and use the actual value
    const isOnTime = true
    return (
      <span className="text-dark-4 text-sm flex items-center gap-1">
        {isOnTime && <Icon name="check" />}
        <span>On time for July 28th scheduled pay date</span>
      </span>
    )
  }, [])

  const description = useMemo(() => {
    if (fundingMethod === 'ach-credit') {
      return 'Here are instructions to transfer funds via ACH Credit. Funds typically appear in your Everlance Wallet in 1-2 business days. We do not accept international transfers.'
    }
    return 'Here are instructions to transfer funds via Wire Transfer.  Funds typically appear in your Everlance Wallet same day. We do not accept international transfers.'
  }, [fundingMethod])

  const bankInfoRowClassName = '!grid-cols-2'

  return (
    <div className="pt-6 flex flex-col gap-6">
      <div className="flex items-center gap-2 text-sm">
        <span className="bg-primary-1 px-2 py-1 rounded-md">{fundingTime}</span>
        {onTimeBrief}
      </div>
      <div className="text-dark">{description}</div>
      <div>
        <div className="grid grid-cols-2 items-center mb-5 last:mb-0 border-b border-dark-1 pb-5">
          <div className="subtitle-2">Amount to fund</div>
          <div>
            <div
              className={cn(
                'w-[200px] text-dark flex items-center cursor-pointer mb-1'
              )}
              onClick={() => copyToClipboard(formattedAmount)}
            >
              <span className="heading-2 my-0 font-bold">
                {formattedAmount}
              </span>
              <Icon
                name="sheets-outline"
                size={18}
                color="text-dark-4"
                className="ml-1"
              />
            </div>
            <div className="subtitle-2 text-dark-4">{amountBrief}</div>
          </div>
        </div>
        <BankInfoRow
          className={bankInfoRowClassName}
          text="Bank name"
          value={bankName}
        />
        <BankInfoRow
          className={bankInfoRowClassName}
          text="Address"
          value={bankAddress}
        />
        <BankInfoRow
          className={bankInfoRowClassName}
          text="ABA / Routing number"
          value={routingNumber}
          isCopyable
        />
        <BankInfoRow
          className={bankInfoRowClassName}
          text="Account number"
          value={accountNumber}
          isCopyable
        />
        <BankInfoRow
          className={bankInfoRowClassName}
          text="Account owner"
          value={accountOwner}
          isCopyable
        />
      </div>
      {fundingInitiated && (
        <div className="bg-secondary-1 rounded-xs text-center pt-10 pb-12 px-12 mt-5">
          {autoPay ? (
            <>
              <div className="subtitle-2 mb-9">
                Please go to your external funding bank to initiate the
                transfer.
              </div>
              <div>
                Once the money arrives to your Everlance wallet, reports will
                autopay.
              </div>
            </>
          ) : (
            <div className="subtitle-2">
              Please go to your funding bank and initiate the transfer.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ACHCreditWire
